/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { useCallback, useMemo, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { isMobile } from 'react-device-detect';
import { OverlayTrigger, Tooltip, Form } from 'react-bootstrap';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Icon from '_components/atomic/Icon';

const { colors, fontSizes, spaces } = theme;

const StyledGroup = styled(
  forwardRef(({ noBottomSpace, ...props }, ref) => (
    <Form.Group ref={ref} {...props} />
  ))
)`
  margin-bottom: ${spaces.regular}px;

  ${({ noBottomSpace }) =>
    noBottomSpace &&
    css`
      margin-bottom: 0;
    `}
`;

const StyledLabel = styled(
  forwardRef(({ centered, color, disabled, size, weight, ...props }, ref) => (
    <Form.Label ref={ref} {...props} />
  ))
)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;

  margin-bottom: 0;

  font-family: 'Aeonik';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: ${colors.gray[700]};

  cursor: pointer;

  ${({ centered }) =>
    centered &&
    css`
      text-align: center;
    `}

  ${({ size }) =>
    size &&
    css`
      font-size: ${size}px;
    `}

  ${({ weight }) =>
    weight &&
    css`
      font-weight: ${weight};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`;

const StyledIconContainer = styled(
  forwardRef(({ color, borderColor, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  display: flex;
  justify-content: center;
  align-items: center;

  height: ${spaces.large}px;
  width: ${spaces.large}px;
  min-height: ${spaces.large}px;
  min-width: ${spaces.large}px;

  margin-right: ${spaces.small - 2}px;

  background: ${colors.white};

  ${({ color }) =>
    color &&
    css`
      background: ${color};
    `}

  ${({ borderColor }) =>
    borderColor &&
    css`
      border-color: ${borderColor};
    `}
`;

const StyledFeedback = styled(
  forwardRef(({ visible, ...props }, ref) => (
    <Form.Control.Feedback ref={ref} {...props} />
  ))
)`
  padding-left: ${spaces.smallest}px;

  font-family: 'Aeonik';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;

  &.processing-feedback {
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${colors.gray[500]};
  }

  &.valid-feedback {
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${colors.gray[500]};
  }

  &.invalid-feedback {
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${colors.indigo[600]};
  }

  @media ${theme.devices.tablet} {
    font-size: ${fontSizes.small}px;
  }
`;

const Checkbox = ({
  name,
  label = 'Label',
  labelAlignCenter,
  labelSize,
  labelWeight,
  hint,
  disabled,
  noBottomSpace,
  textColor,
  boxColor,
  valid,
  invalid,
  feedback,
  value = false,
  onChange,
  ...restProps
}) => {
  const handleChange = useCallback(
    (e) => {
      const { name, checked } = e.target;
      onChange && onChange({ target: { name, value: checked } });
    },
    [onChange]
  );

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? 'top' : 'right-start',
      delay: { show: 0, hide: 0 },
      trigger: ['hover', 'focus'],
      overlay: (
        <Tooltip id={`tooltip-${name}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [name, hint]);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup noBottomSpace={noBottomSpace}>
        <StyledLabel
          htmlFor={name}
          centered={labelAlignCenter}
          color={textColor}
          disabled={disabled}
          size={labelSize}
          weight={labelWeight}
        >
          <Form.Check
            {...restProps}
            hidden
            id={name}
            type="checkbox"
            name={name}
            disabled={disabled}
            checked={value}
            onChange={handleChange}
          />
          <StyledIconContainer>
            {!value && (
              <Icon alias={ICONS_ALIASES.radioOff} color={colors.gray[500]} />
            )}
            {value && (
              <Icon alias={ICONS_ALIASES.radioOn} color={colors.indigo[600]} />
            )}
          </StyledIconContainer>
          {label}
        </StyledLabel>
        <StyledFeedback
          type="processing"
          visible={!valid && !invalid && feedback}
        >
          {feedback}
        </StyledFeedback>
        <StyledFeedback type="valid" visible={valid}>
          {feedback}
        </StyledFeedback>
        <StyledFeedback type="invalid" visible={invalid}>
          <Icon
            alias={ICONS_ALIASES.warning}
            color={theme.colors.indigo[600]}
          />
          {feedback}
        </StyledFeedback>
      </StyledGroup>
    </OverlayTrigger>
  );
};

Checkbox.propTypes = {
  name: PropTypes.string,
  label: PropTypes.node,
  labelAlignCenter: PropTypes.bool,
  labelSize: PropTypes.number,
  labelWeight: PropTypes.number,
  hint: PropTypes.node,
  disabled: PropTypes.bool,
  noBottomSpace: PropTypes.bool,
  textColor: PropTypes.string,
  boxColor: PropTypes.string,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  value: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Checkbox;
